<template>
  <div>
    <b-row>
      <b-col class="col-md-1 mb-1">
        <b-link
            class="btn btn-outline-primary"
            to="/admin-investment-profile"
        >
          <FeatherIcon icon="ChevronLeftIcon" />
          {{ $t("back") }}
        </b-link>
      </b-col>
      <b-col class="col-md-2">
        <h2>{{ $t("investment_profile") }}</h2>
      </b-col>
    </b-row>

    <div v-if="!loading" class="card p-4">
      <h4>User: {{ profile.user.name }} (ID: {{ profile.user.id }})</h4>
      <p><strong>Email:</strong> {{ profile.user.email }}</p>
      <hr />

      <div class="row">
        <div class="col-md-10">
          <div class="row">
            <p class="ml-1"><strong>{{ $t('investment_profile_max_per_invoice') }}: </strong> {{ profile.max_per_invoice }}</p>
            <p class="ml-2"><strong>{{ $t('investment_profile_max_per_seller') }}: </strong> {{ profile.max_per_seller }}</p>
            <p class="ml-2"><strong>{{ $t('investment_profile_max_per_debtor') }}: </strong> {{ profile.max_per_debtor }}</p>
          </div>
          <div class="row">
            <p class="ml-1"><strong>{{ $t('investment_profile_partial_purchase') }}: </strong> {{ profile.partial_purchase ? 'Yes' : 'No' }}</p>
            <p class="ml-2"><strong>{{ $t('investment_profile_maturity') }}: </strong> {{ profile.maturity > 90 ? "> 90" : profile.maturity }} {{ $t('days') }}</p>
            <p class="ml-2"><strong>{{ $t('investment_profile_min_rating') }}: </strong> {{ profile.min_rating }}</p>
          </div>
          <div class="row">
            <p class="ml-1"><strong>{{ $t('investment_profile_company_age') }}: </strong> {{ profile.company_age }}</p>
          </div>
          <div class="row">
            <div class="col-md-5">
              <strong>{{ $t('investment_profile_exclude_industries') }}: </strong>
              <ul>
                <li v-for="industry in profile.exclude_industries" :key="industry.value">
                  {{ $t(industry.value) }}
                </li>
              </ul>
            </div>
            <div class="col-md-5">
              <h5>{{ $t('investment_profile_seller_countries') }}</h5>
              <ul>
                <li
                    v-for="country in profile.seller_countries_details"
                    :key="country.code"
                >
                  {{ $i18n.locale === 'de' ? country.name_de : country.name_en }}
                </li>
              </ul>
            </div>

          </div>

          <div class="row">
            <div class="col-md-5">
              <h5>{{ $t('investment_profile_currencies') }}</h5>
              <ul>
                <li v-for="currency in profile.currencies" :key="currency">
                  {{ currency }}
                </li>
              </ul>
            </div>

            <div class="col-md-5">
              <h5>{{ $t('investment_profile_factoring_type') }}</h5>
              <ul>
                <li v-for="type in profile.factoring_type" :key="type">
                  {{ $t(type) }}
                </li>
              </ul>
            </div>
          </div>

        </div>
        <div class="col-md-2">
          <p><strong>{{ $t('status') }} </strong>
            <span :class="profile.is_active ? 'text-success' : 'text-danger'">
              {{ profile.is_active ? $t('active') : $t('inactive') }}
            </span>
          </p>
          <p>
            <strong>{{ $t('activated_at') }}: </strong>
            <span v-if="profile.is_active">{{ profile.activated_at | formatDate}}</span>
            <span v-else>-</span>
          </p>
          <p>
            <strong>{{ $t('deactivated_at') }}: </strong>
            <span v-if="!profile.is_active">{{ profile.deactivated_at | formatDate }}</span>
          </p>
          <p><strong>{{ $t('investment_profile_email_notifications_label') }}: </strong> {{ profile.email_notifications_enabled ? 'Enabled' : 'Disabled' }}</p>
        </div>
      </div>
      <div class="row">
        <b-button
            size="md"
            class="button-space mt-2 mr-2 ml-auto"
            variant="success"
            @click="loginAsClientFromAdmin(profile)"
            :title="$t('login_as_client')"
        >
          {{ $t('login_as_client') }}
        </b-button>
        <b-button
            v-if="profile.is_active"
            variant="danger"
            class="mt-2"
            @click="$bvModal.show('deactivate-account-modal')"
            size="md"
        >
          {{ $t('deactivate') }}
        </b-button>
      </div>
    </div>
    <div v-else>
      <img src="/loader.svg" alt="Loader">
    </div>

    <b-modal
        id="deactivate-account-modal"
        centered
        hide-footer
        :title="$t('deactivate_account')"
    >
      <b-card-text class="text-left">
        {{ $t('deactivate_account_confirmation') }}
      </b-card-text>

      <b-row>
        <b-col class="text-center">
          <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('deactivate-account-modal')">
            {{ $t("no") }}
          </b-button>
          <b-button variant="danger" @click="deactivateAccount">
            {{ $t("yes") }}
          </b-button>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import {count} from "echarts/lib/component/dataZoom/history";
import {BButton} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: 'InvestorProfile',
  components: {BButton},
  methods: {
    count,
    resolveClientAppOrigin() {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      if (/localhost|127\.0\.0\.1/.test(origin)) {
        return 'http://localhost:8082'
      }
      const stagingSuffix = /staging/i.test(origin) ? '-staging' : ''
      return `https://terminal${stagingSuffix}.aequitex.com`
    },
    loginAsClientFromAdmin(item) {
      this.isLoading = true
      this.items = []
      this.$http
      .post('/loginFromAdmin', {
        email: item.user.email,
      })
      .then(response => {
        if (response.data.status === 200) {
          const token = response.data.value.token
          const base = this.resolveClientAppOrigin()
          const redirectUrl = `${base}/loginFromAdmin/${encodeURIComponent(token)}?isAdminLogin=true`
          window.open(redirectUrl, '_blank')
        }
        this.isLoading = false
      })
      .catch(() => {
        this.isLoading = false
      })
    },
    async deactivateAccount() {
      try {
        const profileID = this.$route.params.id;
        await this.$http.post(`/admin-investment-profile/${profileID}/deactivate`);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("account_deactivated"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        this.$bvModal.hide('deactivate-account-modal');
        setTimeout(() => {
          window.location.reload()
        },1000);
      } catch (err) {
        console.error(err);
      }
    }
  },
  data() {
    return {
      profile: null,
      loading: true,
      error: null,
    };
  },
  async created() {
    const profileID = this.$route.params.id;
    await this.$http.post(`/admin-investment-profile/${profileID}`)
    .then((response) => {
      if (response.data.status === 200) {
        this.profile = response.data.value;
      } else {
        this.error = response.data.message || 'Failed to load profile';
      }
    })
    .catch((err) => {
      this.error = err.response?.data?.message || 'API error';
    })
    .finally(() => {
      this.loading = false;
    });
  },
};
</script>

<style scoped>
</style>
